import React from 'react';
import LocalFilterPanel from '../../../components/common/LocalFilterPanel';

const EventMultiSelect = ({ label, hint, selected, onChange, allActivities, disabled }) => {
  const toggle = (act) => {
    if (selected.includes(act)) {
      onChange(selected.filter((a) => a !== act));
    } else {
      onChange([...selected, act]);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {hint && <span className="ml-1 text-xs text-gray-400">{hint}</span>}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange(allActivities)}
            disabled={disabled || allActivities.length === 0}
            className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40"
          >
            All
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={disabled || selected.length === 0}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-blue-600 mb-1">{selected.length} selected</p>
      )}
      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-1 bg-white">
        {allActivities.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Load chart first to see activities</p>
        ) : (
          allActivities.map((act) => (
            <label key={act} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 rounded">
              <input
                type="checkbox"
                checked={selected.includes(act)}
                onChange={() => toggle(act)}
                disabled={disabled}
                className="rounded"
              />
              <span className="truncate">{act}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

const LeadTimeHistogramSettings = ({
  objectType,
  onObjectTypeChange,
  availableObjectTypes,
  binningMode,
  onBinningModeChange,
  manualMin,
  manualMax,
  manualInterval,
  onManualMinChange,
  onManualMaxChange,
  onManualIntervalChange,
  fileMetadata,
  loading = false,
  localFilters,
  updateLocalFilter,
  activeFile,
  startEvents = [],
  onStartEventsChange,
  endEvents = [],
  onEndEventsChange,
  activitiesForObjectType = [],
  componentTitle,
  onTitleChange,
}) => {

  return (
    <div className="space-y-6">
      {/* Tile Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tile Title
        </label>
        <input
          type="text"
          value={componentTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Lead Time Histogram"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loading}
        />
      </div>

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Object Type
        </label>
        <select
          value={objectType || ""}
          onChange={(e) => onObjectTypeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loading || availableObjectTypes.length === 0}
        >
          {availableObjectTypes.length === 0 && (
            <option value="">Load chart to see types</option>
          )}
          {availableObjectTypes.map((type) => (
            <option key={type} value={type}>
              {type}{" "}
              {fileMetadata?.object_types?.[type] &&
                `(${fileMetadata.object_types[type].toLocaleString()})`}
            </option>
          ))}
        </select>
      </div>

      {/* Start / End Events */}
      {availableObjectTypes.length > 0 && (
        <>
          <div className="text-xs text-gray-500 -mb-2">
            Select start and end events to measure a custom time window. Leave both empty to use the full object lifecycle.
          </div>
          <EventMultiSelect
            label="Start Events"
            hint="(earliest matching event per object)"
            selected={startEvents}
            onChange={onStartEventsChange}
            allActivities={activitiesForObjectType}
            disabled={loading}
          />
          <EventMultiSelect
            label="End Events"
            hint="(latest matching event per object)"
            selected={endEvents}
            onChange={onEndEventsChange}
            allActivities={activitiesForObjectType}
            disabled={loading}
          />
        </>
      )}

      {/* Binning Mode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Binning Mode
        </label>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="auto"
              checked={binningMode === "auto"}
              onChange={(e) => onBinningModeChange(e.target.value)}
              className="mr-2"
            />
            <span className="text-sm">Automatic</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="manual"
              checked={binningMode === "manual"}
              onChange={(e) => onBinningModeChange(e.target.value)}
              className="mr-2"
            />
            <span className="text-sm">Manual</span>
          </label>
        </div>
      </div>

      {/* Manual Binning Fields */}
      {binningMode === "manual" && (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min (hours)
            </label>
            <input
              type="number"
              value={manualMin}
              onChange={(e) => onManualMinChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max (hours)
            </label>
            <input
              type="number"
              value={manualMax}
              onChange={(e) => onManualMaxChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interval (hours)
            </label>
            <input
              type="number"
              value={manualInterval}
              onChange={(e) => onManualIntervalChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="5"
            />
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Local Filter Panel */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['dateRange', 'activityNodes', 'objectAttributes']}
      />
    </div>
  );
};

export default LeadTimeHistogramSettings;
